<script setup>
/**
 * The conditions editor behind the targeting tab's summary card: the six
 * controls that decide when a live-chat campaign fires.
 *
 * Every control writes straight through to the builder's form state, which
 * autosaves - so the dialog has nothing to commit and closes on "Done"
 * rather than offering a Cancel that could not undo anything.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  AVAILABILITIES,
  DEVICE_TYPES,
  FREQUENCIES,
  VISITOR_TYPES,
  WHERE,
  urlPatternValidators,
} from 'dashboard/components-next/Campaigns/helpers/campaignBuilder';

const emit = defineEmits(['close']);

const where = defineModel('where', { type: String, required: true });
const url = defineModel('url', { type: String, required: true });
const visitorType = defineModel('visitorType', {
  type: String,
  required: true,
});
const deviceType = defineModel('deviceType', { type: String, required: true });
const frequency = defineModel('frequency', { type: String, required: true });
const availability = defineModel('availability', {
  type: String,
  required: true,
});
const timeOnPage = defineModel('timeOnPage', {
  type: [String, Number],
  required: true,
});

const { t } = useI18n();

const dialogRef = ref(null);

const segments = [
  {
    key: 'where',
    model: where,
    titleKey: 'CAMPAIGN.BUILDER.CONDITIONS.WHERE.TITLE',
    optionsKey: 'CAMPAIGN.BUILDER.CONDITIONS.WHERE.OPTIONS',
    values: Object.values(WHERE),
  },
  {
    key: 'who',
    model: visitorType,
    titleKey: 'CAMPAIGN.BUILDER.CONDITIONS.WHO.TITLE',
    optionsKey: 'CAMPAIGN.BUILDER.CONDITIONS.WHO.OPTIONS',
    values: Object.values(VISITOR_TYPES),
  },
  {
    key: 'devices',
    model: deviceType,
    titleKey: 'CAMPAIGN.BUILDER.CONDITIONS.DEVICES.TITLE',
    optionsKey: 'CAMPAIGN.BUILDER.CONDITIONS.DEVICES.OPTIONS',
    values: Object.values(DEVICE_TYPES),
  },
  {
    key: 'frequency',
    model: frequency,
    titleKey: 'CAMPAIGN.BUILDER.CONDITIONS.FREQUENCY.TITLE',
    optionsKey: 'CAMPAIGN.BUILDER.CONDITIONS.FREQUENCY.OPTIONS',
    values: Object.values(FREQUENCIES),
  },
  {
    key: 'when',
    model: availability,
    titleKey: 'CAMPAIGN.BUILDER.CONDITIONS.WHEN.TITLE',
    optionsKey: 'CAMPAIGN.BUILDER.CONDITIONS.WHEN.OPTIONS',
    values: Object.values(AVAILABILITIES),
  },
];

const segmentClasses = isActive => [
  'flex-1 h-8 px-2 rounded-md font-medium text-label-small transition-colors',
  isActive
    ? 'bg-n-solid-active text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
    : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12',
];

const isSpecificUrl = computed(() => where.value === WHERE.SPECIFIC_URL);

const urlError = computed(() => {
  if (!isSpecificUrl.value || !url.value) return '';
  const isValid =
    urlPatternValidators.shouldStartWithHTTP(url.value) &&
    urlPatternValidators.shouldBeAValidURLPattern(url.value);
  return isValid ? '' : t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.ERROR');
});

// Switching back to "Everywhere" drops the URL so the payload stops carrying a
// rule the campaign no longer has - the summary and the widget agree either
// way, and switching back to a specific URL starts from a clean field.
const selectWhere = value => {
  where.value = value;
  if (value === WHERE.EVERYWHERE) url.value = '';
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :title="t('CAMPAIGN.BUILDER.CONDITIONS.DIALOG.TITLE')"
    :description="t('CAMPAIGN.BUILDER.CONDITIONS.DIALOG.DESCRIPTION')"
    :show-cancel-button="false"
    :confirm-button-label="t('CAMPAIGN.BUILDER.CONDITIONS.DIALOG.DONE')"
    @confirm="dialogRef?.close()"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5">
      <fieldset
        v-for="segment in segments"
        :key="segment.key"
        class="flex flex-col min-w-0 gap-1.5"
      >
        <legend class="mb-1.5 text-heading-3 text-n-slate-12">
          {{ t(segment.titleKey) }}
        </legend>
        <div
          role="group"
          :aria-label="t(segment.titleKey)"
          class="flex items-center gap-1 p-1 border rounded-lg border-n-weak bg-n-alpha-black2"
        >
          <button
            v-for="value in segment.values"
            :key="value"
            type="button"
            :aria-pressed="segment.model.value === value"
            :class="segmentClasses(segment.model.value === value)"
            @click="
              segment.key === 'where'
                ? selectWhere(value)
                : (segment.model.value = value)
            "
          >
            {{ t(`${segment.optionsKey}.${value.toUpperCase()}`) }}
          </button>
        </div>

        <Input
          v-if="segment.key === 'where' && isSpecificUrl"
          v-model="url"
          type="url"
          class="mt-1.5"
          :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.LABEL')"
          :placeholder="
            t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.PLACEHOLDER')
          "
          :message="urlError"
          :message-type="urlError ? 'error' : 'info'"
        />
      </fieldset>

      <Input
        v-model="timeOnPage"
        type="number"
        min="0"
        :label="t('CAMPAIGN.BUILDER.CONDITIONS.DELAY.LABEL')"
        :placeholder="t('CAMPAIGN.BUILDER.CONDITIONS.DELAY.PLACEHOLDER')"
        :message="t('CAMPAIGN.BUILDER.CONDITIONS.DELAY.HINT')"
        message-type="info"
      />
    </div>
  </Dialog>
</template>
